import { Info } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { trackUIEvent } from '#app/utils/plausible.client.js'
import {
	hasRecentSearchConsent,
	RECENT_SEARCH_CONSENT_COOKIE,
} from '#app/utils/recent-searches.ts'

// Förlängs vid varje besök, som på sajten.
const MAX_AGE_IN_SECONDS = 60 * 60 * 24 * 90

const EXPLAINER =
	'Spara dina sökresultat lokalt i din webbläsare. Det gör att du snabbt hittar tillbaka till dina senaste sökningar.'

function setCookie(value: 0 | 1) {
	document.cookie = `${RECENT_SEARCH_CONSENT_COOKIE}=${value}; path=/; Secure; SameSite=Lax; max-age=${MAX_AGE_IN_SECONDS}`
}

/**
 * Förklaringen bakom en info-ikon: öppnas på hover, fokus och klick (hover
 * finns inte på touch). Hover och klick hålls isär, annars stänger klicket
 * direkt det hovern just öppnat. Portad från sajtens recent-search-consent.tsx.
 */
function Explainer() {
	const [hovered, setHovered] = useState(false)
	const [pinned, setPinned] = useState(false)
	const open = hovered || pinned
	const id = useId()

	return (
		<span className="relative inline-flex">
			<button
				type="button"
				aria-label="Om att spara sökningar"
				aria-expanded={open}
				aria-describedby={open ? id : undefined}
				onClick={() => setPinned(p => !p)}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onFocus={() => setHovered(true)}
				onBlur={() => {
					setHovered(false)
					setPinned(false)
				}}
				className="rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 dark:text-gray-500 dark:hover:text-gray-300"
			>
				<Info className="h-4 w-4" aria-hidden="true" />
			</button>
			{open && (
				// Ovanför kontrollen: foten ligger längst ned i dialogen.
				<span
					id={id}
					role="tooltip"
					className="absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-normal leading-snug text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
				>
					{EXPLAINER}
				</span>
			)}
		</span>
	)
}

/**
 * Opt-in för "Tidigare", en tyst brytare i sökdialogens fot - av tills den
 * slås på. Samma kakkontrakt som sajten ('1' / '0' / saknas = av).
 */
export function RecentSearchToggle() {
	const [enabled, setEnabled] = useState(false)
	const switchId = useId()

	// Kakan finns inte vid SSR; start från false håller server och första
	// klientrendering överens.
	useEffect(() => {
		const consented = hasRecentSearchConsent()
		setEnabled(consented)
		if (consented) setCookie(1)
	}, [])

	const toggle = () => {
		const next = !enabled
		setEnabled(next)
		setCookie(next ? 1 : 0)
		trackUIEvent({
			eventName: 'button_click',
			label: next ? 'recent_searches_on' : 'recent_searches_off',
			location: 'search_modal',
		})
	}

	return (
		<div className="flex items-center gap-2">
			<button
				type="button"
				id={switchId}
				role="switch"
				aria-checked={enabled}
				onClick={toggle}
				className={
					'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ' +
					(enabled ? 'bg-green-700' : 'bg-gray-300 dark:bg-gray-600')
				}
			>
				<span
					aria-hidden="true"
					className={
						'h-4 w-4 rounded-full bg-white shadow transition-transform ' +
						(enabled ? 'translate-x-[1.125rem]' : 'translate-x-0.5')
					}
				/>
			</button>
			{/* Etikett, inte en andra knapp: ett tabbstopp och ett namn för brytaren. */}
			<label
				htmlFor={switchId}
				className="cursor-pointer text-sm text-gray-700 dark:text-gray-300"
			>
				Spara sökningar
			</label>
			<Explainer />
		</div>
	)
}
